import { Checkbox, Field, NumericInput, TextOutput } from 'Components/Core';
import { withInjectedProps } from 'Components/Core/InjectedPropsContext';
import { ChangeEventWithData } from 'Components/Core/types';
import { ListFieldSection } from 'Components/Layout';
import {
  IWorksheetComponent,
  WorksheetBalanceFieldSet,
  WorksheetEquipmentDropdown,
  WorksheetGridFieldSection,
  WorksheetRecordGrid,
} from 'Components/Worksheets/Core';
import { actions } from 'Data/ReduxViewModels/WorksheetEditor';
import { hasValue, valueOrDefault } from 'Lib/Any';
import { Guid } from 'Lib/Guid';
import { combine, pipe } from 'Lib/Hooks/Selectors';
import { joinPaths } from 'Lib/Object/Path/shared';
import { useDispatch, useSelector } from 'react-redux';
import deepEquals from '../../../../../Lib/Object/deepEquals';

let MeasurementsSectionBase: React.FC<IWorksheetComponent> = ({ namePrefix, createWorksheetSelector }) => {
  const bitumenCentrifugeExtractoreId = useSelector(createWorksheetSelector!('BitumenCentrifugeExtractor.Id')) as Guid;
  const centrifugeId = useSelector(createWorksheetSelector!('Centrifuge.Id')) as Guid;
  const needsInitialDrying = useSelector(
    pipe(createWorksheetSelector!('data.Preparation.ContainsMoistureOrVolatileOils', false), valueOrDefault(false))
  );
  const testTakenFromInitialDrying = useSelector(
    pipe(createWorksheetSelector!('TestTakenFromInitialDrying'), valueOrDefault(false))
  );
  const dispatch = useDispatch();
  const trialGroupSize = 2;
  const preventTrialCreation = useSelector(
    pipe(
      createWorksheetSelector!('Trials'),
      valueOrDefault([] as Array<any>),
      (trials) => trials.filter((t) => !t.ExcludedTrial).length >= 2
    ),
    deepEquals
  );

  const trialRecordProps = useSelector(
    pipe(createWorksheetSelector!('Trials'), valueOrDefault([] as Array<any>), (trials) =>
      trials.map((t, i) => ({
        dim: t.ExcludedTrial,
        preventRemove: i < 2,
      }))
    ),
    deepEquals
  );

  const excludeTrials = (i: number, exclude: boolean) => {
    [...Array(trialGroupSize).keys()].forEach((k) =>
      dispatch(
        actions.setValue({
          name: joinPaths(namePrefix, 'Trials', `${i - k}`, 'ExcludedTrial'),
          value: exclude,
          sync: k === trialGroupSize - 1,
        })
      )
    );
  };

  const checkValues = useSelector(
    combine(
      ['FilterBowlMass', 'FilterBowlTestPortionMass'].map((n) => pipe(createWorksheetSelector!(n), hasValue)),
      (hasFilterMass: boolean, hasFilterTestPortinMass: boolean) => hasFilterMass && hasFilterTestPortinMass
    )
  );

  return (
    <>
      <ListFieldSection heading="Binder Extraction">
        <WorksheetBalanceFieldSet name="BalanceBinderExtraction" />
        <Field
          input={<WorksheetEquipmentDropdown />}
          equipmentType="bitumen-centrifuge-extractor"
          name="BitumenCentrifugeExtractor"
          importModel={{ Id: bitumenCentrifugeExtractoreId }}
        />
      </ListFieldSection>
      <Field
        input={<Checkbox />}
        name="TestTakenFromInitialDrying"
        visible={needsInitialDrying}
        confirm={checkValues}
        confirmTitle="Confirm"
        confirmText="The values in both Filter Bowl Mass and Filter Bowl & Test Portion Mass will be cleared, this action cannot be undone. Continue?"
        confirmButtonText="Yes"
      />

      <ListFieldSection>
        <Field input={<NumericInput />} name="FlaskAndStopperMass" visible={!testTakenFromInitialDrying} />
        <Field input={<NumericInput />} name="FilterBowlMass" visible={!testTakenFromInitialDrying} />
        <Field input={<NumericInput />} name="FilterBowlTestPortionMass" visible={!testTakenFromInitialDrying} />
        <Field input={<TextOutput />} name="TestPortionMass" />
        <Field input={<NumericInput />} name="FlaskStopperSolutionMass" />
        <Field input={<TextOutput />} name="SolutionMass" />
      </ListFieldSection>
      <ListFieldSection heading="Binder Separation from Solution">
        <WorksheetBalanceFieldSet name="BalanceBinderSeparation" />
        <Field
          input={<WorksheetEquipmentDropdown />}
          equipmentType="centrifuge"
          name="Centrifuge"
          importModel={{ Id: centrifugeId }}
        />
      </ListFieldSection>
      <WorksheetGridFieldSection name="Trials">
        <WorksheetRecordGrid
          name={'Trials'}
          orientation={'horizontal'}
          recordProps={{
            width: 130,
            preventCreate: preventTrialCreation,
            preventRemove: (i) => trialRecordProps[i]?.preventRemove,
            dim: (i) => trialRecordProps[i]?.dim,
            removeConfirm: {
              confirm: true,
              confirmTitle: 'Trial Deletion',
              confirmText: 'Are you sure you want to delete this pair of trials?',
              confirmButtonText: 'Delete Trial',
            },
            recordGroupingSize: trialGroupSize,
          }}
          fieldSet={[
            {
              name: 'ContainerAndLidMass',
              component: <Field input={<NumericInput />} />,
            },
            {
              name: 'ContainerLidAndLiquidMass',
              component: <Field input={<NumericInput />} />,
            },
            {
              name: 'ContainerLidAndResidueMass',
              component: <Field input={<NumericInput />} />,
            },
            {
              name: 'MassOfBinder',
              component: <Field input={<TextOutput />} />,
            },
            {
              name: 'BinderContentPercentage',
              component: <Field input={<TextOutput />} />,
            },
            {
              name: 'ExcludedTrial',
              component: (i: number) =>
                i % trialGroupSize !== 0 ? (
                  <Field input={<Checkbox />} onChange={(e: ChangeEventWithData) => excludeTrials(i, e?.data?.value)} />
                ) : (
                  <></>
                ),
            },
          ]}
        />
      </WorksheetGridFieldSection>
    </>
  );
};

const MeasurementsSection = withInjectedProps(MeasurementsSectionBase);
export { MeasurementsSection };
export default MeasurementsSection;
